import { createReducer, on } from '@ngrx/store';
import { RequestState } from '@layouts/external-authenticated-layout/store/external-auth.actions';
import { NotificationContent } from '../responses/notifications.response';
import {
  handleFailure,
  handleRequest,
  handleSuccess,
  handleClear,
} from '@shared/ngrx-factory/factory.reducer';
import {
  LoadMyNotificationsActions,
  LoadMyUnreadNotificationsActions,
  LoadMyUnreadNotificationCountActions,
  MarkAllMyNotificationsAsReadActions,
  MarkNotificationAsReadActions,
} from './service-selection-notifications.actions';

export interface ServiceSelectionNotificationsState {
  notifications: RequestState<NotificationContent[]>;
  unreadNotifications: RequestState<NotificationContent[]>;
  unreadNotificationCount: RequestState<number>;
  markAllRead: RequestState<any>;
  markRead: RequestState<any>;
  hasNextAll: boolean;
  hasNextUnread: boolean;
  currentPageAll: number;
  currentPageUnread: number;
}

const initialRequestState = {
  loading: false,
  message: null,
  response: null,
  error: null,
};

const initialState: ServiceSelectionNotificationsState = {
  notifications: { ...initialRequestState, response: [] },
  unreadNotifications: { ...initialRequestState, response: [] },
  unreadNotificationCount: { ...initialRequestState, response: 0 },
  markAllRead: { ...initialRequestState },
  markRead: { ...initialRequestState },
  hasNextAll: false,
  hasNextUnread: false,
  currentPageAll: 0,
  currentPageUnread: 0,
};

export const serviceSelectionNotificationsReducer = createReducer(
  initialState,

  on(LoadMyNotificationsActions.request, (state) => handleRequest(state, 'notifications')),
  on(LoadMyNotificationsActions.success, (state, { response }) => {
    const list = response.page === 0 ? response.content : [...(state.notifications.response || []), ...response.content];
    return {
      ...handleSuccess(state, 'notifications', list),
      hasNextAll: response.hasNext,
      currentPageAll: response.page,
    };
  }),
  on(LoadMyNotificationsActions.failure, (state, { error }) => handleFailure(state, 'notifications', error)),
  on(LoadMyNotificationsActions.clearState, (state) => handleClear(state, 'notifications')),

  on(LoadMyUnreadNotificationsActions.request, (state) => handleRequest(state, 'unreadNotifications')),
  on(LoadMyUnreadNotificationsActions.success, (state, { response }) => {
    const list = response.page === 0 ? response.content : [...(state.unreadNotifications.response || []), ...response.content];
    return {
      ...handleSuccess(state, 'unreadNotifications', list),
      hasNextUnread: response.hasNext,
      currentPageUnread: response.page,
    };
  }),
  on(LoadMyUnreadNotificationsActions.failure, (state, { error }) => handleFailure(state, 'unreadNotifications', error)),
  on(LoadMyUnreadNotificationsActions.clearState, (state) => handleClear(state, 'unreadNotifications')),

  on(LoadMyUnreadNotificationCountActions.request, (state) => handleRequest(state, 'unreadNotificationCount')),
  on(LoadMyUnreadNotificationCountActions.success, (state, { response }) => handleSuccess(state, 'unreadNotificationCount', response)),
  on(LoadMyUnreadNotificationCountActions.failure, (state, { error }) => handleFailure(state, 'unreadNotificationCount', error)),
  on(LoadMyUnreadNotificationCountActions.clearState, (state) => handleClear(state, 'unreadNotificationCount')),

  on(MarkAllMyNotificationsAsReadActions.request, (state) => handleRequest(state, 'markAllRead')),
  on(MarkAllMyNotificationsAsReadActions.success, (state, { response }) => {
    const clearedUnread = { ...state.unreadNotifications, response: [] as NotificationContent[] };
    const clearedCount = { ...state.unreadNotificationCount, response: 0 };
    const updatedNotifications = (state.notifications.response || []).map(n => ({ ...n, read: true }));
    return {
      ...handleSuccess(state, 'markAllRead', response),
      unreadNotifications: clearedUnread,
      unreadNotificationCount: clearedCount,
      notifications: { ...state.notifications, response: updatedNotifications },
    };
  }),
  on(MarkAllMyNotificationsAsReadActions.failure, (state, { error }) => handleFailure(state, 'markAllRead', error)),
  on(MarkAllMyNotificationsAsReadActions.clearState, (state) => handleClear(state, 'markAllRead')),

  on(MarkNotificationAsReadActions.request, (state) => handleRequest(state, 'markRead')),
  on(MarkNotificationAsReadActions.success, (state, { response }) => {
    const markedUuid = response?.data?.uuid;
    let updatedUnread = state.unreadNotifications.response || [];
    let updatedAll = state.notifications.response || [];
    if (markedUuid) {
      updatedUnread = updatedUnread.filter(n => n.uuid !== markedUuid);
      updatedAll = updatedAll.map(n => n.uuid === markedUuid ? { ...n, read: true } : n);
    }
    const updatedCount = Math.max(0, (state.unreadNotificationCount.response || 0) - 1);
    return {
      ...handleSuccess(state, 'markRead', response),
      unreadNotifications: { ...state.unreadNotifications, response: updatedUnread },
      unreadNotificationCount: { ...state.unreadNotificationCount, response: updatedCount },
      notifications: { ...state.notifications, response: updatedAll },
    };
  }),
  on(MarkNotificationAsReadActions.failure, (state, { error }) => handleFailure(state, 'markRead', error)),
  on(MarkNotificationAsReadActions.clearState, (state) => handleClear(state, 'markRead')),
);
